import { cloneDeep, isEqual } from 'lodash'
import intl from 'react-intl-universal'
import { Observable } from 'rxjs'
import { interval } from 'rxjs/observable/interval'
import { publishPermissions } from '~/reactapp/App/Publish'
import { getPublishDateFromRedux } from '~/reactapp/App/PublishDate/PublishDate'
import { errorAction } from '~/redux/commonActions'
import { getSettings } from '~/redux/settingsReducer'
import { Story } from '~/types'
import { ACTION_STORY_LOADED } from '../appReducer'
import { setPublishValidationData } from '../storyData'

export const validateSettingsEpic = (
  action$,
  store,
  { serviceResolver }
): Observable<any> => // eslint-disable-line @typescript-eslint/no-explicit-any
  action$
    .ofType(ACTION_STORY_LOADED)
    .map(() => serviceResolver('storyService'))
    .switchMap((storyService) =>
      Observable.from(interval(100))
        .mergeMap(() => storyService.getStory())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .scan<any>((acc, curr) => {
          if (!isEqual(acc.story, curr)) {
            return {
              story: curr,
              changed: true,
            }
          }
          return {
            story: cloneDeep(acc.story),
            changed: false,
          }
        }, {})
        .filter((scanned) => scanned.changed)
        .map((updated) => updated.story)
        .catch((err) => errorAction(err))
    )
    .map((story: Story) => {
      let validationMsg = ''
      const { canSubsequentPublish, canInitialPublish } = publishPermissions(
        story
      )

      // validate story properties
      if (!story._id) {
        validationMsg = intl.get('publishActions.validation.unsaved')
      } else if (!story.canonical_website) {
        validationMsg = intl.get(
          'publishActions.validation.primaryWebsiteMissing',
          {
            tabName: intl.get('Tabs.planning'),
          }
        )
      } else if (
        story?.additional_properties?.is_published &&
        !canSubsequentPublish
      ) {
        validationMsg = intl.get(
          'publishActions.validation.unauthorizedSubsequentPublish',
          {
            canonicalWebsite: story.canonical_website,
          }
        )
      } else if (
        !story?.additional_properties?.is_published &&
        !canInitialPublish
      ) {
        validationMsg = intl.get(
          'publishActions.validation.unauthorizedInitialPublish',
          {
            canonicalWebsite: story.canonical_website,
          }
        )
      } else {
        // validate story via services
        const reduxState = store.getState()
        const settings = getSettings(reduxState)
        const metaTemplatesService = serviceResolver('metaTemplatesService')
        const validationService = serviceResolver('validationService')

        const requiredFieldsForSave = metaTemplatesService.requiredForSave(
          settings.v2,
          story
        )
        const saveErrors = requiredFieldsForSave.filter(
          (field) => field.missing
        )

        // handle any save errors before publish errors
        if (saveErrors.length > 0) {
          validationMsg = saveErrors
            .map((err) => {
              return intl.get(
                'publishActions.validation.requiredSaveFieldMissing',
                {
                  field: err.label,
                }
              )
            })
            .join(' ')
        } else {
          const requiredFieldsForPublish = metaTemplatesService.requiredForPublish(
            settings.v2,
            story
          )
          const featuredMediaErrors = validationService.validateFeaturedMedia(
            story,
            settings
          )
          const errors = [
            ...requiredFieldsForPublish.filter((field) => {
              if (field.missing) {
                // Validations that check data from outside of Story Object (angular service)
                if (field.key === 'publish_date') {
                  return !getPublishDateFromRedux(reduxState)
                }
              }
              return field.missing
            }),
            ...featuredMediaErrors,
          ]

          validationMsg = errors
            .map((err) =>
              intl.get('publishActions.validation.requiredFieldMissing', {
                field: err.label,
                tabName: err.tab_name || err.tab,
              })
            )
            .join(' ')
        }
      }

      return setPublishValidationData({
        isValid: !validationMsg,
        validationMsg,
      })
    })
